import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Card,
  Empty,
  Input,
  Radio,
  Row,
  Space,
  Spin,
  Steps,
  Table,
  Tabs,
  Tree,
  Typography,
  Upload,
} from 'antd';
import type { TabsProps } from 'antd';
import {
  ArrowLeftOutlined,
  ArrowRightOutlined,
  CheckCircleOutlined,
  CloudDownloadOutlined,
  CloudUploadOutlined,
  CodeOutlined,
  ControlOutlined,
  DeleteOutlined,
  DownloadOutlined,
  ExclamationCircleOutlined,
  FileExcelOutlined,
  FileTextOutlined,
  MergeCellsOutlined,
  PlusOutlined,
  ReloadOutlined,
  SaveOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import { Box, Grid } from '@mui/material';
import * as XLSX from 'xlsx';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import axios from 'axios';
import { useAuthHeader, useAuthUser } from '../../../Services/Auth/sessionAuth';
import { apiEndpoints } from '../../../config';
import { Crypter } from '../../../Functions/Security/Crypter';
import SweetAlert from 'sweetalert2';
import PCEMF from '../../../Models/PCEMF';

type ImportFormat = 'EXCEL' | 'CSV' | 'JSON';
type ExportFormat = 'PDF' | 'EXCEL' | 'CSV' | 'JSON';

interface ImportPCEMFProps {
  onSave?: (data: PCEMF[]) => void;
}

interface TreeNode {
  key: string;
  title: React.ReactNode;
  children?: TreeNode[];
}

interface ValidationIssue {
  Code: string;
  Libelle: string;
  Parent: string | null;
  message: string;
}

const COLUMNS = ['Code', 'Libelle', 'Niveau', 'Parent'];

const sampleData: PCEMF[] = [
  { Code: '1', Libelle: 'COMPTES DE CAPITAUX PERMANENTS', Niveau: 0, Parent: null },
  { Code: '10', Libelle: 'Capital social', Niveau: 1, Parent: '1' },
  { Code: '101', Libelle: 'Capital social - apports en numéraire', Niveau: 2, Parent: '10' },
];

const normalizeRow = (item: any): PCEMF => ({
  Code: String(item.Code ?? item.code ?? '').trim(),
  Libelle: String(item.Libelle ?? item.libelle ?? '').trim(),
  Niveau: Number(item.Niveau ?? item.niveau ?? 0),
  Parent: (item.Parent ?? item.parent ?? null) ? String(item.Parent ?? item.parent) : null,
});

const buildTree = (items: PCEMF[]): TreeNode[] => {
  const build = (parent: string | null): TreeNode[] => {
    return items
      .filter(i => (parent === null ? !i.Parent : i.Parent === parent))
      .sort((a, b) => a.Niveau - b.Niveau || a.Code.localeCompare(b.Code))
      .map(i => ({
        key: i.Code,
        title: (
          <span>
            <b style={{ color: '#116599' }}>{i.Code}</b>
            <span style={{ marginLeft: 8 }}>{i.Libelle}</span>
          </span>
        ),
        children: build(i.Code),
      }));
  };
  return build(null);
};

const computeIssues = (rows: PCEMF[]): ValidationIssue[] => {
  const issues: ValidationIssue[] = [];
  if (!rows.length) return issues;

  const codes = rows.map(r => r.Code);
  const counts = new Map<string, number>();
  codes.forEach(c => counts.set(c, (counts.get(c) || 0) + 1));
  const duplicateCodes = new Set([...counts.entries()].filter(([, n]) => n > 1).map(([c]) => c));
  const knownCodes = new Set(codes);

  rows.forEach(r => {
    const messages: string[] = [];
    if (!r.Code) messages.push('Code manquant.');
    if (!r.Libelle) messages.push('Libellé manquant.');
    if (r.Parent && !knownCodes.has(r.Parent)) messages.push(`Parent inconnu : ${r.Parent}.`);
    if (r.Code && duplicateCodes.has(r.Code)) messages.push(`Code dupliqué : ${r.Code}.`);

    if (messages.length) {
      issues.push({
        Code: r.Code || '-',
        Libelle: r.Libelle || '-',
        Parent: r.Parent,
        message: messages.join(' '),
      });
    }
  });

  return issues;
};

const ImportPCEMF = ({ onSave }: ImportPCEMFProps) => {
  const authHeader = useAuthHeader();
  const auth = useAuthUser();
  const LIRE_URL = `${apiEndpoints.pcemf}/Lire`;
  const GERER_URL = `${apiEndpoints.pcemf}/Gerer`;

  const [format, setFormat] = useState<ImportFormat>('EXCEL');
  const [exportFormat, setExportFormat] = useState<ExportFormat>('EXCEL');
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<PCEMF[]>([]);
  const [success, setSuccess] = useState(false);
  const [fileName, setFileName] = useState('');
  const [searchText, setSearchText] = useState('');
  const [saveAction, setSaveAction] = useState<'ECRASER' | 'MANQUANTS' | 'FUSIONNER' | null>(null);

  const treeData = useMemo(() => buildTree(data), [data]);
  const issues = useMemo(() => computeIssues(data), [data]);
  const hasErrors = issues.length > 0;

  const filteredData = useMemo(() => {
    const term = searchText.toLowerCase().trim();
    if (!term) return data;
    return data.filter(d =>
      d.Code.toLowerCase().includes(term) ||
      d.Libelle.toLowerCase().includes(term) ||
      (d.Parent ?? '').toLowerCase().includes(term)
    );
  }, [data, searchText]);

  const parseRows = (rows: any[][], file: string) => {
    if (rows.length < 2) {
      SweetAlert.fire({ title: 'Fichier vide', text: 'Aucune donnée exploitable.', icon: 'warning' });
      return;
    }

    const header = rows[0].map((h: any) => String(h).trim().toLowerCase());
    const idxCode = Math.max(0, header.findIndex(h => h.includes('code')));
    const idxLibelle = Math.max(1, header.findIndex(h => h.includes('libelle')));
    const idxNiveau = Math.max(2, header.findIndex(h => h.includes('niveau')));
    const idxParent = Math.max(3, header.findIndex(h => h.includes('parent')));

    const parsed: PCEMF[] = [];
    for (let i = 1; i < rows.length; i++) {
      const row = rows[i];
      if (!row || row.length === 0) continue;
      const code = String(row[idxCode] ?? '').trim();
      const libelle = String(row[idxLibelle] ?? '').trim();
      if (!code && !libelle) continue;

      parsed.push({
        Code: code,
        Libelle: libelle,
        Niveau: Number(row[idxNiveau] ?? 0) || 0,
        Parent: row[idxParent] ? String(row[idxParent]).trim() : null,
      });
    }

    setData(parsed);
    setFileName(file);
    setStep(1);
    setSuccess(false);
  };

  const parseJSON = (text: string, file: string) => {
    try {
      const raw = JSON.parse(text);
      const arr = Array.isArray(raw) ? raw : raw.data ?? [];
      const parsed: PCEMF[] = arr.map((item: any) => normalizeRow(item)).filter((r: PCEMF) => r.Code || r.Libelle);
      setData(parsed);
      setFileName(file);
      setStep(1);
      setSuccess(false);
    } catch (e) {
      SweetAlert.fire({ title: 'Erreur JSON', text: 'Le fichier JSON est invalide.', icon: 'error' });
    }
  };

  const handleUpload = (file: File) => {
    setLoading(true);
    setFileName(file.name);

    if (format === 'EXCEL') {
      const reader = new FileReader();
      reader.onload = (e) => {
        const buffer = e.target?.result as ArrayBuffer;
        const workbook = XLSX.read(buffer, { type: 'array' });
        const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json(firstSheet, { header: 1 }) as any[][];
        parseRows(rows, file.name);
        setLoading(false);
      };
      reader.readAsArrayBuffer(file);
    }

    if (format === 'CSV') {
      const reader = new FileReader();
      reader.onload = (e) => {
        const text = String(e.target?.result ?? '');
        const delimiter = text.includes(';') ? ';' : ',';
        const lines = text.split(/\r?\n/).filter(l => l.trim());
        const rows = lines.map(line => line.split(delimiter).map(c => c.trim()));
        parseRows(rows, file.name);
        setLoading(false);
      };
      reader.readAsText(file);
    }

    if (format === 'JSON') {
      const reader = new FileReader();
      reader.onload = (e) => {
        const text = String(e.target?.result ?? '');
        parseJSON(text, file.name);
        setLoading(false);
      };
      reader.readAsText(file);
    }

    return false;
  };

  const handleDownloadModel = () => {
    if (format === 'EXCEL') {
      const ws = XLSX.utils.aoa_to_sheet([COLUMNS, ...sampleData.map(r => [r.Code, r.Libelle, r.Niveau, r.Parent ?? ''])]);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'PCEMF');
      const out = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      const blob = new Blob([out], { type: 'application/octet-stream' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'modele_pcemf.xlsx';
      a.click();
      URL.revokeObjectURL(url);
    }

    if (format === 'CSV') {
      const content = [COLUMNS.join(';'), ...sampleData.map(r => [r.Code, r.Libelle, r.Niveau, r.Parent ?? ''].join(';'))].join('\n');
      const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'modele_pcemf.csv';
      a.click();
      URL.revokeObjectURL(url);
    }

    if (format === 'JSON') {
      const content = JSON.stringify(sampleData, null, 2);
      const blob = new Blob([content], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'modele_pcemf.json';
      a.click();
      URL.revokeObjectURL(url);
    }
  };

  const handleDownloadValidated = () => {
    const content = JSON.stringify(data, null, 2);
    const blob = new Blob([content], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `pcemf_${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleDownloadPCEMF = () => {
    if (data.length === 0) {
      SweetAlert.fire({ title: 'Aucune donnée', text: 'Aucun PCEMF à télécharger.', icon: 'warning' });
      return;
    }
    const fileDate = new Date().toISOString().slice(0, 10);
    if (exportFormat === 'EXCEL') {
      const ws = XLSX.utils.aoa_to_sheet([COLUMNS, ...data.map(r => [r.Code, r.Libelle, r.Niveau, r.Parent ?? ''])]);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'PCEMF');
      const out = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      const blob = new Blob([out], { type: 'application/octet-stream' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `pcemf_${fileDate}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    }
    if (exportFormat === 'CSV') {
      const content = [COLUMNS.join(';'), ...data.map(r => [r.Code, r.Libelle, r.Niveau, r.Parent ?? ''].join(';'))].join('\n');
      const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `pcemf_${fileDate}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    }
    if (exportFormat === 'JSON') {
      const content = JSON.stringify(data, null, 2);
      const blob = new Blob([content], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `pcemf_${fileDate}.json`;
      a.click();
      URL.revokeObjectURL(url);
    }
    if (exportFormat === 'PDF') {
      const doc = new jsPDF({ orientation: 'landscape' });
      doc.setFontSize(14);
      doc.text('Plan comptable PCEMF', 14, 15);
      doc.setFontSize(10);
      doc.text(`Généré le ${fileDate}`, 14, 23);
      autoTable(doc, {
        startY: 30,
        head: [COLUMNS],
        body: data.map(r => [r.Code, r.Libelle, String(r.Niveau), r.Parent ?? '']),
        theme: 'striped',
        headStyles: { fillColor: [17, 101, 153] },
        styles: { fontSize: 9, cellPadding: 2 },
      });
      doc.save(`pcemf_${fileDate}.pdf`);
    }
  };

  const loadPCEMF = async () => {
    setLoading(true);
    try {
      const res = await axios.get(LIRE_URL, {
        headers: { Authorization: authHeader(), 'Content-Type': 'application/json' }
      });
      const raw = JSON.parse(Crypter.decrypt(res.data));
      const decrypted = raw.Value ?? raw;
      if (decrypted.StatutCode === 200) {
        const list: PCEMF[] = decrypted.Result?.Table || [];
        setData(list);
        setStep(list.length ? 1 : 0);
      } else {
        SweetAlert.fire({ title: 'Erreur', text: decrypted.Message, icon: 'error' });
      }
    } catch (ex: any) {
      SweetAlert.fire({ title: 'Erreur', text: ex.message, icon: 'error' });
    }
    setLoading(false);
  };

  const saveToApi = async (action: string) => {
    setLoading(true);
    try {
      const utilisateur = (auth() as any)?.NomUser ?? '';
      const payload = { Action: action, Utilisateur: utilisateur, Items: data };
      const cypher = Crypter.encrypt(JSON.stringify(payload));
      const res = await axios.post(GERER_URL, JSON.stringify(cypher), {
        headers: { Authorization: authHeader(), 'Content-Type': 'application/json' }
      });
      const raw = JSON.parse(Crypter.decrypt(res.data));
      const decrypted = raw.Value ?? raw;
      if (decrypted.StatutCode === 200) {
        SweetAlert.fire({ title: 'Succès', text: decrypted.Message, icon: 'success' });
        setSuccess(true);
        setStep(3);
        if (onSave) onSave(data);
      } else {
        SweetAlert.fire({ title: 'Erreur', text: decrypted.Message, icon: 'error' });
      }
    } catch (ex: any) {
      SweetAlert.fire({ title: 'Erreur', text: ex.message, icon: 'error' });
    }
    setLoading(false);
  };

  const handleContinue = () => {
    if (hasErrors) {
      SweetAlert.fire({ title: 'Validation impossible', text: 'Veuillez corriger les problèmes détectés avant de continuer.', icon: 'warning' });
      return;
    }
    setSaveAction(null);
    setStep(2);
  };

  const handleConfirmSave = () => {
    if (!saveAction) {
      SweetAlert.fire({ title: 'Attention', text: 'Veuillez choisir une stratégie d\'enregistrement.', icon: 'warning' });
      return;
    }

    SweetAlert.fire({
      title: `Confirmer l'importation (${saveAction})`,
      text: `${data.length} comptes seront traités.`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#116599',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Oui, enregistrer',
    }).then((r) => {
      if (r.isConfirmed) saveToApi(saveAction);
    });
  };

  useEffect(() => {
    loadPCEMF();
  }, []);

  const handleReset = () => {
    setData([]);
    setSuccess(false);
    setFileName('');
    setSearchText('');
    setSaveAction(null);
    setStep(0);
  };

  const updateRow = (record: PCEMF, field: keyof PCEMF, value: any) => {
    const idx = data.indexOf(record);
    if (idx === -1) return;
    const next = [...data];
    next[idx] = {
      ...next[idx],
      [field]: field === 'Niveau' ? (Number(value) || 0) : (field === 'Parent' ? (value || null) : value),
    };
    setData(next);
  };

  const removeRow = (record: PCEMF) => {
    const idx = data.indexOf(record);
    if (idx === -1) return;
    setData(data.filter((_, i) => i !== idx));
  };

  const issueCodes = useMemo(() => new Set(issues.map(i => i.Code)), [issues]);

  const columns = [
    {
      title: 'État',
      key: 'etat',
      width: 60,
      render: (_: any, record: PCEMF) =>
        issueCodes.has(record.Code) ? (
          <WarningOutlined style={{ color: '#ff4d4f' }} />
        ) : null,
    },
    { title: 'Code', dataIndex: 'Code', key: 'Code', width: 110 },
    {
      title: 'Libellé',
      dataIndex: 'Libelle',
      key: 'Libelle',
      width: 500,
      render: (_: any, record: PCEMF) => (
        <Input
          style={{ width: '100%' }}
          defaultValue={record.Libelle}
          onBlur={(e) => updateRow(record, 'Libelle', e.target.value)}
        />
      ),
    },
    {
      title: 'Niveau',
      dataIndex: 'Niveau',
      key: 'Niveau',
      width: 100,
      render: (_: any, record: PCEMF) => (
        <Input
          style={{ width: '100%' }}
          type='number'
          defaultValue={record.Niveau}
          onBlur={(e) => updateRow(record, 'Niveau', e.target.value)}
        />
      ),
    },
    {
      title: 'Parent',
      dataIndex: 'Parent',
      key: 'Parent',
      width: 120,
      render: (_: any, record: PCEMF) => (
        <Input
          style={{ width: '100%' }}
          defaultValue={record.Parent ?? ''}
          onBlur={(e) => updateRow(record, 'Parent', e.target.value)}
        />
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 100,
      render: (_: any, record: PCEMF) => (
        <Button danger size='small' icon={<DeleteOutlined />} onClick={() => removeRow(record)} />
      ),
    },
  ];

  const issueColumns = [
    { title: 'Code', dataIndex: 'Code', key: 'Code', width: 120 },
    { title: 'Libellé', dataIndex: 'Libelle', key: 'Libelle' },
    { title: 'Parent', dataIndex: 'Parent', key: 'Parent', width: 120, render: (p: any) => p ?? '-' },
    {
      title: 'Détail du problème',
      dataIndex: 'message',
      key: 'message',
      render: (text: string) => (
        <span style={{ color: '#cf1322' }}>
          <ExclamationCircleOutlined style={{ marginRight: 6 }} />
          {text}
        </span>
      ),
    },
  ];

  const steps = [
    { title: 'Import', icon: <CloudUploadOutlined /> },
    { title: 'Prévisualisation', icon: <FileTextOutlined /> },
    { title: 'Stratégie', icon: <ControlOutlined /> },
    { title: 'Validation', icon: <CheckCircleOutlined /> },
  ];

  const formatDescriptions: Record<ImportFormat, { icon: React.ReactNode; title: string; text: string; example: string }> = {
    EXCEL: {
      icon: <FileExcelOutlined style={{ color: '#116599' }} />,
      title: 'Fichier Excel',
      text: 'Première ligne : Code, Libelle, Niveau, Parent. Les données commencent à la ligne 2.',
      example: 'A1: Code\nB1: Libelle\nC1: Niveau\nD1: Parent',
    },
    CSV: {
      icon: <FileTextOutlined style={{ color: '#116599' }} />,
      title: 'Fichier CSV',
      text: 'Séparateur accepté : point-virgule (;) ou virgule (,). Première ligne : Code;Libelle;Niveau;Parent.',
      example: 'Code;Libelle;Niveau;Parent\n1;COMPTES DE CAPITAUX;0;\n10;Capital social;1;1',
    },
    JSON: {
      icon: <CodeOutlined style={{ color: '#116599' }} />,
      title: 'Fichier JSON',
      text: 'Tableau d\'objets { code, libelle, niveau, parent }. Les clés peuvent être en majuscules ou minuscules.',
      example: '[\n  { "code": "1", "libelle": "Capitaux", "niveau": 0, "parent": null }\n]',
    },
  };

  const previewTabs: TabsProps['items'] = [
    {
      key: 'table',
      label: 'Tableau éditable',
      children: data.length ? (
        <>
          <Input.Search
            allowClear
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            placeholder='Rechercher par code, libellé ou parent'
            style={{ width: '100%', marginBottom: 12 }}
          />
          <Table
            dataSource={filteredData}
            columns={columns}
            rowKey={(record, index) => `${record.Code}-${index}`}
            pagination={{ pageSize: 20 }}
            size='small'
            tableLayout='fixed'
            scroll={{ x: 'max-content', y: 450 }}
            onRow={(record) => ({
              style: { backgroundColor: issueCodes.has(record.Code) ? '#fff1f0' : undefined },
            })}
          />
        </>
      ) : (
        <Empty description='Aucune donnée importée' />
      ),
    },
    {
      key: 'tree',
      label: 'Arborescence',
      children: (
        <Box style={{ height: 450, overflow: 'auto', padding: 8 }}>
          {treeData.length ? (
            <Tree showLine treeData={treeData} />
          ) : (
            <Empty description='Aucune hiérarchie à afficher' />
          )}
        </Box>
      ),
    },
    {
      key: 'issues',
      label: (
        <span>
          <ExclamationCircleOutlined style={{ marginRight: 4 }} />
          Problèmes
          {issues.length > 0 && <Badge count={issues.length} style={{ marginLeft: 8 }} color='red' />}
        </span>
      ),
      children: issues.length ? (
        <Table
          dataSource={issues}
          columns={issueColumns}
          rowKey={(record) => `${record.Code}-${record.message}`}
          pagination={false}
          size='small'
          scroll={{ y: 450 }}
        />
      ) : (
        <Empty description='Aucun problème détecté' />
      ),
    },
  ];

  const accept = {
    EXCEL: '.xlsx,.xls',
    CSV: '.csv',
    JSON: '.json',
  }[format];

  return (
    <div className='tab'>
      <Typography.Title level={3} style={{ textAlign: 'center', color: '#0d4a70' }}>
        <CloudUploadOutlined style={{ marginRight: 12 }} />
        IMPORT DU PLAN COMPTABLE EMF (PCEMF)
      </Typography.Title>

      <Steps current={step} items={steps} style={{ marginBottom: 24, marginTop: 12 }} />

      <Spin spinning={loading} size='large' tip='Lecture du fichier en cours...'>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <Box className='bordure1' style={{ padding: 16, height: '100%' }}>
              <Typography.Text strong style={{ color: '#0d4a70' }}>1. Choisir le format</Typography.Text>
              <Radio.Group
                value={format}
                onChange={(e) => {
                  setFormat(e.target.value);
                  setData([]);
                  setSuccess(false);
                  setSearchText('');
                  setSaveAction(null);
                  setStep(0);
                }}
                style={{ width: '100%', marginTop: 12 }}
              >
                <Space direction='vertical'>
                  <Radio value='EXCEL'>{formatDescriptions.EXCEL.icon} Excel (.xlsx, .xls)</Radio>
                  <Radio value='CSV'>{formatDescriptions.CSV.icon} CSV (.csv)</Radio>
                  <Radio value='JSON'>{formatDescriptions.JSON.icon} JSON (.json)</Radio>
                </Space>
              </Radio.Group>

              <Box style={{ marginTop: 20, padding: 12, backgroundColor: 'rgba(17,101,153,0.08)', borderRadius: 8 }}>
                <Typography.Text strong>{formatDescriptions[format].title}</Typography.Text>
                <p style={{ margin: '8px 0 0', fontSize: 13 }}>{formatDescriptions[format].text}</p>
                <pre style={{ background: '#f5f5f5', padding: 8, borderRadius: 6, fontSize: 12, marginTop: 10 }}>
                  {formatDescriptions[format].example}
                </pre>
              </Box>

              <Upload
                accept={accept}
                showUploadList={false}
                beforeUpload={handleUpload}
                multiple={false}
              >
                <Button
                  size='large'
                  icon={<CloudUploadOutlined />}
                  style={{ marginTop: 20, width: '100%', backgroundColor: '#116599', color: 'white' }}
                >
                  Sélectionner un fichier
                </Button>
              </Upload>

              <Button
                icon={<DownloadOutlined />}
                onClick={handleDownloadModel}
                style={{ marginTop: 10, width: '100%' }}
              >
                Télécharger le modèle
              </Button>

              <Button
                icon={<CloudDownloadOutlined />}
                onClick={loadPCEMF}
                style={{ marginTop: 10, width: '100%' }}
              >
                Charger les PCEMF enregistrés
              </Button>

              <Box style={{ marginTop: 16, padding: 12, backgroundColor: 'rgba(17,101,153,0.04)', borderRadius: 8 }}>
                <Typography.Text strong style={{ color: '#0d4a70' }}>Télécharger les PCEMF présents</Typography.Text>
                <Radio.Group
                  value={exportFormat}
                  onChange={(e) => setExportFormat(e.target.value)}
                  style={{ width: '100%', marginTop: 8 }}
                >
                  <Space direction='vertical'>
                    <Radio value='EXCEL'>Excel (.xlsx)</Radio>
                    <Radio value='CSV'>CSV (.csv)</Radio>
                    <Radio value='JSON'>JSON (.json)</Radio>
                    <Radio value='PDF'>PDF (.pdf)</Radio>
                  </Space>
                </Radio.Group>
                <Button
                  icon={<DownloadOutlined />}
                  onClick={handleDownloadPCEMF}
                  disabled={data.length === 0}
                  style={{ marginTop: 12, width: '100%' }}
                >
                  Télécharger ({data.length} compte{data.length > 1 ? 's' : ''})
                </Button>
              </Box>

              {fileName && (
                <Box style={{ marginTop: 16, padding: 8, background: '#edf5fa', borderRadius: 6 }}>
                  <Typography.Text type='secondary'>Fichier : </Typography.Text>
                  <Typography.Text strong>{fileName}</Typography.Text>
                </Box>
              )}
            </Box>
          </Grid>

          <Grid item xs={12} md={8}>
            <Box className='bordure1' style={{ padding: 16, minHeight: 540 }}>
              {step === 0 && (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description='Veuillez sélectionner un format et importer un fichier PCEMF.'
                />
              )}

              {step === 1 && (
                <>
                  <Row justify='space-between' align='middle' style={{ marginBottom: 12 }}>
                    <Typography.Text strong style={{ color: '#0d4a70' }}>
                      2. Prévisualisation et modification ({data.length} compte{data.length > 1 ? 's' : ''})
                    </Typography.Text>
                    <Space>
                      <Button icon={<ReloadOutlined />} onClick={handleReset}>Réinitialiser</Button>
                      <Button icon={<ArrowRightOutlined />} type='primary' onClick={handleContinue} disabled={hasErrors}>
                        {hasErrors ? 'Corrigez les problèmes' : 'Continuer'}
                      </Button>
                    </Space>
                  </Row>

                  {hasErrors && (
                    <Alert
                      type='warning'
                      showIcon
                      icon={<ExclamationCircleOutlined />}
                      message={`${issues.length} problème(s) détecté(s)`}
                      description="Consultez l'onglet Problèmes pour la liste détaillée."
                      style={{ marginBottom: 12 }}
                    />
                  )}

                  <Tabs defaultActiveKey='table' items={previewTabs} />
                </>
              )}

              {step === 2 && (
                <Box style={{ padding: 8 }}>
                  <Row justify='space-between' align='middle' style={{ marginBottom: 16 }}>
                    <Typography.Text strong style={{ color: '#0d4a70' }}>3. Choix de la stratégie d'enregistrement</Typography.Text>
                    <Button icon={<ArrowLeftOutlined />} onClick={() => setStep(1)}>Retour</Button>
                  </Row>

                  <Grid container spacing={2} style={{ marginBottom: 16 }}>
                    <Grid item xs={12} md={4}>
                      <Card
                        hoverable
                        onClick={() => setSaveAction('ECRASER')}
                        style={{ borderColor: saveAction === 'ECRASER' ? '#116599' : undefined, height: '100%' }}
                      >
                        <DeleteOutlined style={{ fontSize: 28, color: '#116599' }} />
                        <Typography.Title level={5} style={{ color: '#0d4a70', marginTop: 12 }}>ÉCRASER</Typography.Title>
                        <Typography.Text type='secondary'>Remplace entièrement le plan comptable existant par les nouvelles données.</Typography.Text>
                      </Card>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Card
                        hoverable
                        onClick={() => setSaveAction('MANQUANTS')}
                        style={{ borderColor: saveAction === 'MANQUANTS' ? '#116599' : undefined, height: '100%' }}
                      >
                        <PlusOutlined style={{ fontSize: 28, color: '#116599' }} />
                        <Typography.Title level={5} style={{ color: '#0d4a70', marginTop: 12 }}>MANQUANTS</Typography.Title>
                        <Typography.Text type='secondary'>Insère uniquement les comptes qui n'existent pas encore.</Typography.Text>
                      </Card>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Card
                        hoverable
                        onClick={() => setSaveAction('FUSIONNER')}
                        style={{ borderColor: saveAction === 'FUSIONNER' ? '#116599' : undefined, height: '100%' }}
                      >
                        <MergeCellsOutlined style={{ fontSize: 28, color: '#116599' }} />
                        <Typography.Title level={5} style={{ color: '#0d4a70', marginTop: 12 }}>FUSIONNER</Typography.Title>
                        <Typography.Text type='secondary'>Met à jour les comptes existants et ajoute les nouveaux.</Typography.Text>
                      </Card>
                    </Grid>
                  </Grid>

                  {saveAction && (
                    <Alert
                      type='info'
                      showIcon
                      message={`Action sélectionnée : ${saveAction}`}
                      description='Vérifiez votre choix avant de confirmer.'
                      style={{ marginBottom: 16 }}
                    />
                  )}

                  <Row justify='end'>
                    <Button icon={<SaveOutlined />} type='primary' onClick={handleConfirmSave} disabled={!saveAction}>
                      Valider et enregistrer
                    </Button>
                  </Row>
                </Box>
              )}

              {step === 3 && success && (
                <Card style={{ marginTop: 16, textAlign: 'center', backgroundColor: '#edf5fa' }}>
                  <CheckCircleOutlined style={{ fontSize: 48, color: '#116599' }} />
                  <Typography.Title level={4} style={{ color: '#0d4a70' }}>Import enregistré</Typography.Title>
                  <Typography.Text>{data.length} comptes ont été traités dans le core-banking.</Typography.Text>
                  <br />
                  <Button icon={<DownloadOutlined />} onClick={handleDownloadValidated} style={{ marginTop: 16 }}>
                    Télécharger le JSON validé
                  </Button>
                </Card>
              )}
            </Box>
          </Grid>
        </Grid>
      </Spin>
    </div>
  );
};

export default ImportPCEMF;
